import { HTMLTemplateElement, isDefined, isInstanceOf } from "@acng/frontend-bounty/types";
import { isCommentNode, isElementNode, isTextNode } from "@acng/frontend-bounty/dom/type.js";
import { before, createText, removeNode, setAttribute, whenAll } from "@acng/frontend-bounty";
import { pushToArray } from "@acng/frontend-bounty/array";
import { parseJSON } from "@acng/frontend-bounty/parse";
import { STRING, typeguard } from "@acng/frontend-bounty/typeguard.js";
import { createElement, parentElement, replace as replaceNode, setText } from "@acng/frontend-bounty";
import { ObjectStore } from "@acng/frontend-bounty/object.js";
import { RESOURCE_COMPILE, REGISTRY_ELEMENT, UUID_STARGAZER_LOADER } from "../model/resource.js";
import { element, render, scope } from "./render.js";
import { rootNamespace } from "./root.js";
import { lookup } from "./lookup.js";
const MODULE = "stargazer/compile";
const REGEXP = /([#$]){([^}]*)}#?({)?/g;
const REGEXP_JSON = /({.*?})#/g;
//const REGEXP_DIR = /#{([^}]*)}/g;
const REGEXP_ID = /(\w[\w-]*)/g;
/**
 * @typedef {import('../model/resource.js').ResourceElement} ResourceElement
 */
/**
 * @internal
 * @param {string} name
 * @param {ElementCompiler} compiler
 * @returns {void}
 */
export const defineElementCompiler = (name, compiler) => {
    ASSERT: if (isDefined(compileElements[name]))
        throw Error();
    compileElements[name] = compiler;
};
/**
 * @internal
 * @param {string} name
 * @param {CompileAttrFn} compiler
 * @returns {void}
 */
export const defineAttrCompiler = (name, compiler) => {
    compileAttrs[name] = compiler;
};
/**
 * TODO mark the resource element with an attibute if there is
 * TODO nothing to render (nodes, params, controllers) data-render="NPC"
 * @internal
 * @param {ResourceElement} resource
 * @returns {Promise<void>}
 */
export const compile = async (resource) => {
    if (!resource[RESOURCE_COMPILE]) {
        DEBUG: if (resource.hasAttribute("debug")) {
            performance.mark(`${MODULE}:started`);
            console.info(MODULE, { resource });
        }
        /** @type {Promise<unknown>[]} */
        const promises = [];
        compileChildNodes(resource, promises, resource.content);
        resource[RESOURCE_COMPILE] = whenAll(promises);
        DEBUG: if (resource.hasAttribute("debug")) {
            resource[RESOURCE_COMPILE].then(() => {
                console.info(`${MODULE} compiled`, {
                    duration: performance.measure(MODULE, `${MODULE}:started`).duration,
                });
            });
        }
    }
    await resource[RESOURCE_COMPILE];
};
/**
 * @param {import("../model/resource.js").ResourceElement} resource
 * @param {Promise<unknown>[]} promises
 * @param {ParentNode} node
 * @ŧodo TODO detect and break loops
 */
const compileChildNodes = (resource, promises, node) => {
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const childNode = node.childNodes[i];
        if (isTextNode(childNode)) {
            compileTextNode(resource, childNode, promises);
        }
        else if (isInstanceOf(childNode, HTMLTemplateElement)) {
            pushToArray(promises, compile(childNode));
        }
        else if (isElementNode(childNode)) {
            compileElement(resource, childNode, promises, node);
        }
        else if (isCommentNode(childNode)) {
            removeNode(childNode);
        }
    }
};
/**
 * @param {import("../model/resource.js").ResourceElement} resource
 * @param {Text} node
 * @param {Promise<unknown>[]} promises
 */
const compileTextNode = (resource, node, promises) => {
    const text = node.nodeValue;
    // Typescript thinks that nodeValue of Text might be null.
    ASSERT: typeguard(`${MODULE} Text.nodeValue`, text, STRING);
    /** @type {RegExpExecArray | null} */
    let match;
    let lastMatch = 0;
    REGEXP.lastIndex = 0;
    while ((match = REGEXP.exec(text))) {
        const [, prefix, dir, suffix] = match;
        /** @type {import("./render").Scope | undefined} */
        let params;
        if (match.index > lastMatch) {
            before(node, text.substring(lastMatch, match.index));
        }
        if (suffix) {
            REGEXP_JSON.lastIndex = REGEXP.lastIndex - 1;
            match = REGEXP_JSON.exec(text);
            if (match) {
                params = parseParameters(match[1]);
                REGEXP.lastIndex = REGEXP_JSON.lastIndex;
            }
        }
        lastMatch = REGEXP.lastIndex;
        if (prefix != "#") {
            const variable = parseValue(dir);
            if (variable) {
                // resource[RESOURCE_TEXT] = true; TODO maybe add data-attribute to resource
                before(node, variable);
            }
            continue;
        }
        const asyncReplace = createText(dir);
        const path = parseDirective(dir);
        before(node, asyncReplace);
        pushToArray(promises, replace(resource, asyncReplace, path, params));
        REGEXP.lastIndex = lastMatch;
    }
    if (lastMatch < text.length) {
        before(node, text.substring(lastMatch));
    }
    removeNode(node);
};
/**
 * @param {string} json
 * @returns {import("./render").Scope | undefined}
 */
const parseParameters = (json) => {
    try {
        const params = parseJSON(json);
        return params;
    }
    catch (reason) {
        DEBUG: console.error(`${MODULE} ignoring parameters with syntax error`, { reason, json });
    }
};
/**
 * @param {string} data
 */
const parseValue = (data) => {
    REGEXP_ID.lastIndex = 0;
    const match = REGEXP_ID.exec(data);
    if (match) {
        return `\${${match[1]}}`;
    }
};
//const REGEXP_UP = /\.(\.)?\//g;
/**
 * @param {string} data
 *
 * TODO add base param to allow relative directive. add origin-url as symbol property to each template?
 * TODO detect loader type and rearrange to path array
 */
const parseDirective = (data) => {
    /** @type {RegExpExecArray | null} */
    let match;
    REGEXP_ID.lastIndex = 0;
    /*
    REGEXP_UP.lastIndex = 0;
    while ((match = REGEXP_UP.exec(data))) {
      if (match[1]) {
        dir = sliceOfArray(dir, 0, -1);
      }
      REGEXP_ID.lastIndex = REGEXP_UP.lastIndex;
    }
    if (REGEXP_ID.lastIndex == 0) {
      dir = [];
    }
    */
    /**
     * @type {string[]}
     */
    const dir = [];
    while ((match = REGEXP_ID.exec(data))) {
        pushToArray(dir, match[1]);
    }
    return dir;
};
/**
 * @todo
 * - {@link CompileFn} priorities
 *
 * @param {ResourceElement} resource
 * @param {Element} element
 * @param {Promise<unknown>[]} promises
 * @param {ParentNode} parentNode - Required to conditionally skip the subtree.
 */
const compileElement = (resource, element, promises, parentNode) => {
    const { attributes } = element;
    // Expand attributes.
    for (let i = attributes.length - 1; i >= 0; i--) {
        const { name } = attributes[i];
        if (name[0] === ":") {
            const parts = name.split(":");
            for (let i = 1, n = ""; i < parts.length - 1; i++) {
                n += `:${parts[i]}`;
                setAttribute(element, n, parts[i + 1]);
            }
        }
    }
    // Compile attributes.
    for (let i = attributes.length - 1; i >= 0; i--) {
        const compileAttr = compileAttrs[attributes[i].name];
        if (compileAttr) {
            compileAttr(element, attributes[i], promises);
            if (element.parentNode !== parentNode) {
                return;
            }
        }
    }
    // Compile element.
    const compileName = getCompileFn(element.localName);
    if (compileName) {
        compileName(element, promises);
        if (element.parentNode !== parentNode) {
            return;
        }
    }
    compileChildNodes(resource, promises, element);
};
/**
 * @todo
 * - [ ] Use HiddenResource exception
 * - [ ] Improve error handling
 *
 * @param {HTMLTemplateElement} resource
 * @param {ChildNode} childNode
 * @param {string[]} path
 *
 * @param {import("./render").Scope | undefined} replacements
 *
 * @returns {Promise<void>}
 */
const replace = async (resource, childNode, path, replacements) => {
    try {
        DEBUG: if (resource.hasAttribute("debug"))
            console.debug(`${MODULE}`, { resource, childNode });
        const childResource = await lookup(rootNamespace, path);
        const fragment = render({
            [REGISTRY_ELEMENT]: childResource,
            [scope]: replacements,
            [element]: parentElement(childNode) ?? resource,
        });
        DEBUG: {
            if (childResource.hasAttribute("debug")) {
                console.info(`${MODULE} cascade debug mode to another resource`, { resource, childResource });
                resource.setAttribute("debug", "");
            }
        }
        replaceNode(childNode, fragment);
    }
    catch (reason) {
        if (isInstanceOf(reason, HTMLTemplateElement)) {
            return;
        }
        console.error(UUID_STARGAZER_LOADER, { reason, resource, childNode });
        // Add some verbose stuff to the resource because some devs do not read console output.
        // resource `path` not found, which is included in `resource`
        const errorElement = createElement("strong");
        Object.assign(errorElement.style, {
            background: "#000",
            border: "2px dashed #ff0",
            outline: "2px solid #000",
            color: "#ff0",
            padding: "5px 10px",
            "border-radius": "5px",
        });
        setText(errorElement, `🚧 ${childNode.textContent} 🚧`);
        replaceNode(childNode, errorElement);
    }
};
const getCompileFn = ObjectStore(
/**
 * @param {string} name
 */
(name) => compileElements[name]?.(name) ?? null);
/**
 * @type {Record<string, ?ElementCompiler>}
 */
const compileElements = {};
/**
 * @type {Record<string, CompileAttrFn | undefined>}
 */
const compileAttrs = {};
/**
 * The `Compiler` may provide a unique closure to `CompileFn`.
 * Such as registering a custom element, or dynamic module `import(...)`,
 * or loading an external resource.
 *
 * @internal
 * @callback ElementCompiler
 * @param {string} name
 * @returns {CompileElementFn}
 */
/**
 * Compile Time DOM Manipulation
 * - Inserting elements is only allowed directly before or after the `element` or inside the subtree.
 * - So removing the element means it's gone from the template.
 *
 * @callback CompileElementFn
 * @param {Element} element
 * @param {Promise<any>[]} promises
 * @returns {void}
 */
/**
 * @callback CompileAttrFn
 * @param {Element} element
 * @param {Attr} attr
 * @param {Promise<any>[]} promises
 * @returns {void}
 */
